/**
 * Conversión y formato de moneda — réplica de la fórmula de la app móvil
 * (apps/mobile/utils/currency.ts): tasa base USD→DOP de 62 con margen del 3%.
 *
 * IMPORTANTE: cada vehículo/repuesto guarda su precio en su PROPIA moneda
 * (campo `currency` en el documento de Firestore). Por eso la conversión
 * necesita la moneda de origen, no se asume DOP.
 */
export type Currency = "DOP" | "USD";

/**
 * Modo de visualización elegido por el usuario.
 * `ORIGINAL` muestra cada precio en la moneda en que está guardado.
 */
export type CurrencyMode = Currency | "ORIGINAL";

const USD_DOP_BASE = 62;
const MARGIN = 0.03;

export const CURRENCY_SYMBOL: Record<Currency, string> = {
  DOP: "RD$",
  USD: "US$",
};

/** Normaliza un valor arbitrario de moneda a 'DOP' | 'USD' (default DOP). */
export function normalizeCurrency(value: string | null | undefined): Currency {
  return value === "USD" ? "USD" : "DOP";
}

/**
 * Convierte un monto de una moneda a otra.
 * - USD → DOP: tasa de venta (margen hacia arriba).
 * - DOP → USD: tasa de compra (margen hacia abajo).
 */
export function convert(amount: number, from: Currency, to: Currency): number {
  if (!amount || from === to) return Math.round(amount || 0);
  if (from === "USD" && to === "DOP") {
    return Math.round(amount * (USD_DOP_BASE * (1 + MARGIN)));
  }
  if (from === "DOP" && to === "USD") {
    return Math.round(amount / (USD_DOP_BASE * (1 - MARGIN)));
  }
  return Math.round(amount);
}

/**
 * Formatea un monto para mostrarlo. `sourceCurrency` es la moneda en que el
 * precio está almacenado; `mode` el modo elegido por el usuario
 * (`'DOP'`, `'USD'` o `'ORIGINAL'` = la moneda original de cada producto).
 */
export function formatPrice(
  amount: number | null | undefined,
  sourceCurrency: Currency,
  mode: CurrencyMode
): string {
  if (amount == null || !Number.isFinite(amount) || amount <= 0) {
    return "Consultar";
  }
  const display: Currency = mode === "ORIGINAL" ? sourceCurrency : mode;
  const value = convert(amount, sourceCurrency, display);
  const formatted = value.toLocaleString(display === "USD" ? "en-US" : "es-DO");
  return `${CURRENCY_SYMBOL[display]} ${formatted}`;
}
